import type { WoodPalette } from '@bn-games/render-helpers';
import type { Theme } from '../settings/themes.js';

/**
 * Canvas-render palette consumed by every domino renderer. Flat token list
 * built from `Theme` so renderers don't reach into the theme structure
 * directly — keeps the theme schema independent of the render side.
 */
export interface RenderPalette {
  readonly name: string;

  /** Wood palette consumed by `getWoodPattern` / `getBorderWoodPattern`. */
  readonly wood: WoodPalette;

  /** Brass accents used for the frame ornaments. */
  readonly brassHi: string;
  readonly brass: string;
  readonly brassDeep: string;

  readonly tableBase: string;
  readonly tableDeep: string;
  readonly tableEdge: string;

  readonly handZoneBg: string;
  readonly handZoneBorder: string;

  readonly tileFaceBase: string;
  readonly tileFaceHighlight: string;
  readonly tileFaceShadow: string;
  readonly tileFaceBorder: string;

  readonly tileDivider: string;

  readonly pipColor: string;
  readonly pipShadow: string;

  readonly tileBackBase: string;
  readonly tileBackPattern: string;
  readonly tileBackBorder: string;

  readonly selectedRing: string;
  readonly validArmGlow: string;
  readonly hoverGlow: string;

  readonly textPrimary: string;
  readonly textSecondary: string;

  readonly bannerBg: string;
  readonly bannerBorder: string;
  readonly bannerTextColor: string;
}

export function themeToRenderPalette(theme: Theme): RenderPalette {
  return {
    name: theme.name,

    wood: {
      name: theme.name,
      boardWoodBase: theme.boardWoodBase,
      boardWoodDark: theme.boardWoodDark,
      boardWoodLight: theme.boardWoodLight,
      borderWoodBase: theme.borderWoodBase,
      borderWoodDark: theme.borderWoodDark,
      borderWoodLight: theme.borderWoodLight,
    },

    brassHi: theme.brassHi,
    brass: theme.brass,
    brassDeep: theme.brassDeep,

    tableBase: theme.feltBase,
    tableDeep: theme.feltDark,
    tableEdge: theme.borderWoodDark,

    handZoneBg: 'rgba(0, 0, 0, 0.18)',
    handZoneBorder: theme.rule,

    tileFaceBase: theme.tileFaceBase,
    tileFaceHighlight: theme.tileFaceHighlight,
    tileFaceShadow: theme.tileFaceShadow,
    tileFaceBorder: theme.tileFaceBorder,

    tileDivider: theme.tileDivider,

    pipColor: theme.pipColor,
    pipShadow: theme.pipShadow,

    tileBackBase: theme.tileBackBase,
    tileBackPattern: theme.tileBackPattern,
    tileBackBorder: theme.tileBackBorder,

    selectedRing: theme.selectedRing,
    validArmGlow: theme.validArmGlow,
    hoverGlow: theme.hoverGlowColor,

    textPrimary: theme.ink,
    textSecondary: theme.inkSoft,

    bannerBg: theme.bannerBg,
    bannerBorder: theme.bannerBorder,
    bannerTextColor: theme.bannerTextColor,
  };
}
